import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import StructuredData from '@/components/feature/StructuredData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const siteUrl = 'https://777cinema.com';
  const pageUrl = `${siteUrl}/about`;

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormSubmitting(false);
      return;
    }

    const formData = new FormData(form);
    if (honeypotField) {
      formData.delete('website_alt');
    }

    try {
      const resp = await fetch('https://readdy.ai/api/form/d9nh1f4it35ii3jdlr5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || responseText || '提交失败';
        setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    } finally {
      setFormSubmitting(false);
    }
  }, []);

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '777影院',
      url: siteUrl,
      description: '777影院提供高清免费影视资源，电影、电视剧、综艺全集在线观看，超清画质，流畅不卡顿，免费无广告，极速播放。',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@777cinema.com',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '关于777影院',
      url: pageUrl,
      description: '了解777影院品牌故事、平台优势和联系方式',
      isPartOf: { '@type': 'WebSite', name: '777影院', url: siteUrl },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '777影院需要付费吗？',
          acceptedAnswer: { '@type': 'Answer', text: '777影院所有影视资源完全免费，无需付费，无需开通会员，打开即看。我们通过广告合作维持平台运营，但绝不会在播放过程中插入广告打扰您的观影体验。' },
        },
        {
          '@type': 'Question',
          name: '777影院的画质怎么样？',
          acceptedAnswer: { '@type': 'Answer', text: '777影院提供超清画质，支持1080P和4K分辨率播放。我们采用先进的视频编码技术和全球CDN加速节点，确保您在任何设备上都能享受流畅清晰的观影体验。' },
        },
        {
          '@type': 'Question',
          name: '777影院有广告吗？',
          acceptedAnswer: { '@type': 'Answer', text: '777影院承诺观影全程无广告打扰。页面展示少量静态推荐内容，但播放过程中不会弹出任何视频广告、贴片广告或弹窗广告，确保纯净的观影体验。' },
        },
        {
          '@type': 'Question',
          name: '777影院支持哪些设备？',
          acceptedAnswer: { '@type': 'Answer', text: '777影院支持电脑、平板、手机等多种设备在线观看。网站采用响应式设计，自动适配不同屏幕尺寸。推荐使用Chrome、Safari、Edge等现代浏览器获得最佳体验。' },
        },
        {
          '@type': 'Question',
          name: '777影院的资源更新速度如何？',
          acceptedAnswer: { '@type': 'Answer', text: '777影院拥有专业的资源更新团队，热门影视通常在首播后24小时内上线，确保您能在第一时间观看到最新内容。每日更新量超过50部。' },
        },
      ],
    },
  ];

  return (
    <>
      <SEOMeta
        title="关于777影院 - 品牌介绍与联系方式 | 777影院高清免费"
        description="了解777影院品牌故事与发展历程。777影院是国内领先的高清免费影视平台，提供电影、电视剧、综艺在线观看服务，超清画质，流畅不卡顿。"
        keywords="777影院,关于777影院,777影院介绍,免费影视平台,在线观看"
        canonicalUrl={pageUrl}
        ogType="website"
      />
      <StructuredData data={structuredData} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
          </div>

          {/* Hero */}
          <section className="relative py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20cinema%20lobby%20interior%20with%20warm%20ambient%20lighting%2C%20modern%20design%20with%20clean%20lines%2C%20soft%20gold%20and%20cream%20color%20palette%2C%20cozy%20yet%20sophisticated%20atmosphere%2C%20architectural%20photography%20style%2C%20peaceful%20and%20inviting%20space%2C%20no%20people&width=1800&height=600&seq=about-hero-01&orientation=landscape"
              alt="777影院品牌"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
            <div className="relative z-10 w-full px-4 md:px-6 text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">关于777影院</h1>
              <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto">
                用心打造纯粹的高清免费观影平台
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-12 md:py-16">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">品牌故事</h2>
                <p className="text-sm text-foreground-500">始于热爱，忠于品质</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-xl mb-4">
                    <i className="ri-lightbulb-line text-xl text-primary-500"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-base">我们的初心</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    777影院成立于2020年，由一群热爱影视的年轻人共同创立。我们深知用户在寻找高质量影视资源时的种种困扰——广告弹窗、卡顿画面、低清画质、付费墙……于是我们决定打造一个真正以用户体验为核心的免费影视平台。让每一个人，无论身处何地，都能轻松享受到超清流畅的观影体验。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-50 rounded-xl mb-4">
                    <i className="ri-group-line text-xl text-accent-500"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-base">我们的团队</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的团队由技术工程师、内容编辑和产品设计师组成，分布在全国各地。技术人员负责维护全球CDN节点和视频编码优化，确保播放流畅；内容编辑每日精心筛选和整理优质影视资源；设计师持续优化界面交互，让每一次访问都变成享受。我们虽然是小团队，但每个人都对这份事业充满热情。
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-50 rounded-xl mb-4">
                  <i className="ri-eye-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-base">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们希望777影院成为全球华人首选的免费影视平台。不仅提供海量的影视资源，更通过技术创新不断提升观影体验。我们相信，好的内容应该被更多人看到，而不应该被付费墙阻挡。未来，我们将继续坚持&ldquo;免费、高清、无广告&rdquo;的核心理念，让777影院陪伴每一位用户的休闲时光。
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '50万+', label: '影视资源', icon: 'ri-film-line' },
                  { value: '1000万+', label: '月活用户', icon: 'ri-user-line' },
                  { value: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
                  { value: '4.9', label: '用户评分', icon: 'ri-star-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-50 rounded-xl">
                      <i className={`${stat.icon} text-xl text-accent-500`}></i>
                    </div>
                    <div className="font-heading text-2xl md:text-3xl font-bold text-accent-600 mb-1">{stat.value}</div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="py-12 md:py-16">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">用户口碑</h2>
                <p className="text-sm text-foreground-500">听听用户怎么说</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { name: '张先生', role: '电影爱好者', content: '用了777影院两年多了，画质真的没话说，关键是免费还没广告，良心平台！推荐给了身边所有朋友。', rating: 5 },
                  { name: '李女士', role: '追剧达人', content: '更新速度超快，热门剧集基本都是当天就能看。界面也很清爽，找剧很方便，已经安利给全家人了。', rating: 5 },
                  { name: '王同学', role: '大学生', content: '作为学生党，免费太重要了！777影院资源丰富，动漫、综艺应有尽有，宿舍追番神器。', rating: 5 },
                ].map((review) => (
                  <div key={review.name} className="bg-background-100 rounded-lg p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{review.content}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-primary-600">{review.name[0]}</span>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-foreground-900">{review.name}</div>
                        <div className="text-[10px] text-foreground-500">{review.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-16 bg-background-100">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">常见问题</h2>
                <p className="text-sm text-foreground-500">关于777影院的常见疑问</p>
              </div>

              <div className="space-y-4">
                {[
                  { q: '777影院需要付费吗？', a: '777影院所有影视资源完全免费，无需付费，无需开通会员，打开即看。我们通过广告合作维持平台运营，但绝不会在播放过程中插入广告打扰您的观影体验。' },
                  { q: '777影院的画质怎么样？', a: '777影院提供超清画质，支持1080P和4K分辨率播放。我们采用先进的视频编码技术和全球CDN加速节点，确保您在任何设备上都能享受流畅清晰的观影体验。' },
                  { q: '777影院有广告吗？', a: '777影院承诺观影全程无广告打扰。页面展示少量静态推荐内容，但播放过程中不会弹出任何视频广告、贴片广告或弹窗广告，确保纯净的观影体验。' },
                  { q: '777影院支持哪些设备？', a: '777影院支持电脑、平板、手机等多种设备在线观看。网站采用响应式设计，自动适配不同屏幕尺寸。推荐使用Chrome、Safari、Edge等现代浏览器获得最佳体验。' },
                  { q: '777影院的资源更新速度如何？', a: '777影院拥有专业的资源更新团队，热门影视通常在首播后24小时内上线，确保您能在第一时间观看到最新内容。每日更新量超过50部。' },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70">
                    <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-medium text-foreground-900 list-none">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 md:py-16">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">联系我们</h2>
                <p className="text-sm text-foreground-500">有任何问题或建议，欢迎随时联系我们</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                {[
                  { icon: 'ri-mail-line', title: '电子邮件', value: 'contact@777cinema.com' },
                  { icon: 'ri-customer-service-line', title: '在线客服', value: '7x24小时在线服务' },
                  { icon: 'ri-map-pin-line', title: '公司地址', value: '中国北京市朝阳区光华路9号' },
                ].map((contact) => (
                  <div key={contact.title} className="bg-background-100 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-primary-50 rounded-xl">
                      <i className={`${contact.icon} text-xl text-primary-500`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-sm">{contact.title}</h3>
                    <p className="text-sm text-foreground-600">{contact.value}</p>
                  </div>
                ))}
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-lg p-6 md:p-8 max-w-2xl mx-auto">
                <h3 className="font-heading font-semibold text-foreground-950 mb-6 text-base text-center">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="website-alt-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 outline-none transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 outline-none transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 outline-none transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div className="mb-5">
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 outline-none transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-medium px-6 py-3 rounded-full text-sm transition-colors duration-200 whitespace-nowrap"
                  >
                    {formSubmitting ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="mt-4 text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="mt-4 text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}